import { BossKind, EnemyType, GameMode } from '../constants';
import { getDifficulty } from '../data/difficulty';
import { getStage, StageCueKind, type StageSpec } from '../data/stages';
import type { GameWorld } from '../world/GameWorld';
import { GATE_APPROACH_TICKS, nextGateTicks } from './gateCue';

/** 敵不在時の戦闘時計倍率。scrollX は実時間どおり別途1回だけ進める。 */
const EMPTY_FIELD_STAGE_TICKS = 3;

export class StageDirector {
  stageId = 0;
  stageTick = 0;
  cueIndex = 0;
  scrollSpeed = 0;
  musicLayer = 0;
  quiet = false;
  bossGate = false;
  finalBossStarted = false;
  completed = false;
  /** R2 Gate pass edge for the read-only render family; reset per stage. */
  gatePassSerial = 0;
  spec: StageSpec = getStage(0);
  private timelineScale = 1;

  /** R2専用の道中圧縮率。正本stage dataとlegacy経路のtickは変更しない。 */
  setTimelineScale(scale: number): void {
    this.timelineScale = Math.max(0.1, Math.min(1, scale));
  }

  scaledTick(tick: number): number {
    return Math.max(1, Math.round(tick * this.timelineScale));
  }

  get durationTicks(): number {
    return this.scaledTick(this.spec.durationTicks);
  }

  /** 直前R2版の5秒から半分へ。legacyは従来の300tickを維持する。 */
  get gateApproachTicks(): number {
    return Math.round(GATE_APPROACH_TICKS * Math.min(1, this.timelineScale * 2));
  }

  start(stageId: number): void {
    this.stageId = stageId;
    this.spec = getStage(stageId);
    this.stageTick = 0;
    this.cueIndex = 0;
    this.scrollSpeed = this.spec.baseScroll;
    this.musicLayer = 0;
    this.quiet = false;
    this.bossGate = false;
    this.finalBossStarted = false;
    this.completed = false;
    this.gatePassSerial = 0;
  }

  update(world: GameWorld): void {
    // HOTPATH-BEGIN
    if (this.completed || world.mode !== GameMode.Playing) return;
    if (this.bossGate) {
      if (world.bosses.active) return;
      this.bossGate = false;
      if (this.finalBossStarted) {
        this.completed = true;
        world.finishStage();
        return;
      }
    }
    let stageTicks = 1;
    if (!this.quiet && !world.hasCombatPressure()) {
      const gateTicks = nextGateTicks(this);
      const gateApproachTicks = this.gateApproachTicks;
      if (gateTicks < 0) {
        stageTicks = EMPTY_FIELD_STAGE_TICKS;
      } else if (gateTicks > gateApproachTicks) {
        // Gate 接近窓を飛び越さず、R2/legacyそれぞれの実時間で開始する。
        stageTicks = Math.min(EMPTY_FIELD_STAGE_TICKS, gateTicks - gateApproachTicks);
      }
    }
    this.stageTick += stageTicks;
    world.scrollX += this.scrollSpeed;
    const cues = this.spec.cues;
    while (this.cueIndex < cues.length) {
      const cue = cues[this.cueIndex];
      if (!cue || this.scaledTick(cue.tick) > this.stageTick) break;
      this.cueIndex += 1;
      switch (cue.kind) {
        case StageCueKind.SpawnWave: {
          const density = getDifficulty(world.difficulty).spawnDensity;
          let count = Math.max(1, Math.round(cue.b * density));
          if (count > 24) count = 24;
          world.spawnWave(cue.a as EnemyType, count, cue.c);
          break;
        }
        case StageCueKind.Gate:
          if (world.continuousArsenalEnabled) {
            this.gatePassSerial = (this.gatePassSerial + 1) >>> 0;
            world.triggerContinuousGateSurge();
            break;
          }
          world.enterCalibrationGate();
          return;
        case StageCueKind.MusicLayer:
          this.musicLayer = cue.a;
          world.musicLayer = cue.a;
          break;
        case StageCueKind.ScrollChange:
          this.scrollSpeed = this.spec.baseScroll * (cue.a / 100);
          break;
        case StageCueKind.MidBoss:
          world.bosses.start(cue.a as BossKind, world);
          this.bossGate = true;
          return;
        case StageCueKind.FinalBoss:
          world.bosses.start(cue.a as BossKind, world);
          this.bossGate = true;
          this.finalBossStarted = true;
          return;
        case StageCueKind.Quiet:
          this.quiet = true;
          this.scrollSpeed *= 0.4;
          world.musicLayer = 0;
          break;
      }
    }
    if (this.stageTick >= this.durationTicks && !this.bossGate) {
      this.completed = true;
      world.finishStage();
    }
    // HOTPATH-END
  }
}
